import clone from 'lodash/clone'
import uniqBy from 'lodash/uniqBy'
import { DateTime } from 'luxon'

/**
 * This function fixes a bug where all sessions in a multi-session group event
 * show in a coach's schedule as starting on the same day.
 *
 * uniqBy removes the "duplicated" GroupEvent records from bookings, and then
 * groupEventList creates a list of GroupEvents with the correct startDateTime
 * and endDateTime attributes by cloning the original GroupEvent and updating
 * the times with the values from each session.
 */
export const bookingsWithSessionStartTimes = (bookings, timeframe) => {
  const uniqueBookings = uniqBy(bookings, 'id')
  const resultingBookings = []

  uniqueBookings.forEach(booking =>
    isGroupEvent(booking)
      ? resultingBookings.push(groupEventList(booking, timeframe))
      : resultingBookings.push(booking),
  )

  const flattenedBookings = resultingBookings.flat()
  return flattenedBookings.filter(booking => isInTimeframe(booking, timeframe))
}

function isGroupEvent(booking) {
  return booking.__typename === 'GroupEvent'
}

function groupEventList(groupEvent) {
  const sessions = groupEvent.sessions
  if (!sessions || sessions.length === 0) {
    return [groupEvent]
  }

  const newEvents = []

  sessions.forEach(session => {
    newEvents.push(cloneGroupEventFromSession(groupEvent, session))
  })

  return newEvents
}

function cloneGroupEventFromSession(groupEvent, session) {
  const clonedEvent = clone(groupEvent)
  clonedEvent.startDateTime = session.startDateTime
  clonedEvent.endDateTime = session.endDateTime
  clonedEvent.currentSession = session

  return clonedEvent
}

function isOnToday(booking) {
  if (!booking?.startDateTime || !booking?.endDateTime) {
    return false
  }

  const zone = booking.timezone
  const start = zone
    ? DateTime.fromISO(booking.startDateTime, { zone })
    : DateTime.fromISO(booking.startDateTime)
  const end = zone
    ? DateTime.fromISO(booking.endDateTime, { zone })
    : DateTime.fromISO(booking.endDateTime)

  if (!start.isValid || !end.isValid) {
    return false
  }

  const todayStart = zone
    ? DateTime.now()
      .setZone(zone)
      .startOf('day')
    : DateTime.local().startOf('day')
  const todayEnd = todayStart.endOf('day')

  return start <= todayEnd && end >= todayStart
}

export const bookingsForTodaySchedule = bookings => {
  const uniqueBookings = uniqBy(bookings, 'id')
  const flattened = uniqueBookings.flatMap(booking =>
    isGroupEvent(booking) ? groupEventList(booking) : [booking],
  )

  return flattened
    .filter(booking => isOnToday(booking))
    .sort(
      (a, b) =>
        DateTime.fromISO(a.startDateTime).toMillis() -
        DateTime.fromISO(b.startDateTime).toMillis(),
    )
}

export const bookingsUpcoming = (bookings, { limit = 4 } = {}) => {
  const now = DateTime.now()
  const uniqueBookings = uniqBy(bookings, 'id')
  const flattened = uniqueBookings.flatMap(booking =>
    isGroupEvent(booking) ? groupEventList(booking) : [booking],
  )

  return flattened
    .filter(booking => {
      if (booking.isCanceled || !booking.endDateTime) {
        return false
      }

      const end = booking.timezone
        ? DateTime.fromISO(booking.endDateTime, { zone: booking.timezone })
        : DateTime.fromISO(booking.endDateTime)

      return end.isValid && end > now
    })
    .sort(
      (a, b) =>
        DateTime.fromISO(a.startDateTime).toMillis() -
        DateTime.fromISO(b.startDateTime).toMillis(),
    )
    .slice(0, limit)
}

function isInTimeframe(booking, timeframe) {
  if (timeframe === 'today') {
    return isOnToday(booking)
  }

  const isPast = new Date(booking.endDateTime).getTime() < Date.now()
  return timeframe === 'past' ? isPast : !isPast
}

function bookingOverlapsRange(booking, rangeStart, rangeEnd) {
  if (!booking?.startDateTime || !booking?.endDateTime) {
    return false
  }

  const zone = booking.timezone || rangeStart.zoneName
  const start = DateTime.fromISO(booking.startDateTime, { zone })
  const end = DateTime.fromISO(booking.endDateTime, { zone })

  if (!start.isValid || !end.isValid) {
    return false
  }

  // Compare calendar bounds in the booking's facility timezone so midnight
  // events near a day/week cusp are not dropped when the browser zone differs
  // (e.g. America/Denver viewing an America/Chicago 12:00 AM Sunday lesson).
  const rangeStartInZone = rangeStart.setZone(zone).startOf('day')
  const rangeEndInZone = rangeEnd.setZone(zone).endOf('day')

  return start <= rangeEndInZone && end >= rangeStartInZone
}

export const flattenBookings = bookings => {
  const uniqueBookings = uniqBy(bookings, 'id')

  return uniqueBookings.flatMap(booking =>
    isGroupEvent(booking) ? groupEventList(booking) : [booking],
  )
}

export const bookingsInDateRange = (bookings, rangeStart, rangeEnd) =>
  flattenBookings(bookings)
    .filter(booking => bookingOverlapsRange(booking, rangeStart, rangeEnd))
    .sort(
      (a, b) =>
        DateTime.fromISO(a.startDateTime).toMillis() -
        DateTime.fromISO(b.startDateTime).toMillis(),
    )
